import {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import type ErgAuthService from '@ergeon/erg-auth';

import {reportError} from '../../../utils/reporting';
import {hasLiveSession, watchUnauthorizedResponses} from '../../../utils/sessionExpiry';

type SessionNotice = 'none' | 'shown' | 'dismissed';

type SessionExpiry = {
  sessionNoticeVisible: boolean;
  showSessionNotice(): void;
  dismissSessionNotice(): void;
  checkSessionQuietly(): void;
  onUnauthorized(): void;
};

const tabIsVisible = () => document.visibilityState === 'visible';

function useWhenTabBecomesVisible(callback: () => void) {
  useEffect(() => {
    const callWhenVisible = () => {
      if (tabIsVisible()) {
        callback();
      }
    };
    document.addEventListener('visibilitychange', callWhenVisible);

    return () => {
      document.removeEventListener('visibilitychange', callWhenVisible);
    };
  }, [callback]);
}

function useSessionExpiry(authService: ErgAuthService): SessionExpiry {
  const [notice, setNotice] = useState<SessionNotice>('none');
  const currentNotice = useRef<SessionNotice>('none');
  const rejectedTokenReported = useRef(false);

  const moveNoticeTo = useCallback((next: SessionNotice) => {
    if (currentNotice.current === next) {
      return;
    }

    currentNotice.current = next;
    setNotice(next);
  }, []);

  const checkSessionQuietly = useCallback(() => {
    if (hasLiveSession(authService)) {
      moveNoticeTo('none');
      return;
    }

    if (currentNotice.current === 'none') {
      moveNoticeTo('shown');
    }
  }, [authService, moveNoticeTo]);

  const checkSessionAloud = useCallback(() => {
    if (hasLiveSession(authService)) {
      moveNoticeTo('none');
      return;
    }

    moveNoticeTo('shown');
  }, [authService, moveNoticeTo]);

  const reportRejectedTokenOnce = useCallback(() => {
    if (rejectedTokenReported.current) {
      return;
    }

    rejectedTokenReported.current = true;
    reportError(new Error('An API answered 401 under an unexpired ergauth token'), {
      operation: 'auth_unauthorized_with_live_token',
      handled: true,
    });
  }, []);

  const onUnauthorized = useCallback(() => {
    if (hasLiveSession(authService)) {
      reportRejectedTokenOnce();
    }
    checkSessionAloud();
  }, [authService, checkSessionAloud, reportRejectedTokenOnce]);

  const showSessionNotice = useCallback(() => moveNoticeTo('shown'), [moveNoticeTo]);
  const dismissSessionNotice = useCallback(() => moveNoticeTo('dismissed'), [moveNoticeTo]);

  useWhenTabBecomesVisible(checkSessionAloud);
  useEffect(() => watchUnauthorizedResponses(axios, onUnauthorized), [onUnauthorized]);

  const sessionNoticeVisible = notice === 'shown';
  return {sessionNoticeVisible, showSessionNotice, dismissSessionNotice, checkSessionQuietly, onUnauthorized};
}

export default useSessionExpiry;
